import React, { useCallback } from "react";
import classNames from "classnames";
import { format, parseISO } from "date-fns";
import { CellContext } from "@tanstack/react-table";
import { RowExpansion } from "@design-system/ViewTable/RowExpansion";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { accountingNumberFormatter } from "@common/helpers/number";
import { getShortJournalEntryReferenceNumber } from "@common/helpers/accounting";
import { AvatarTooltip } from "@components/avatar/AvatarTooltip";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useEntitiesById } from "@hooks/useEntities";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import { useOverlays } from "contexts/OverlayContext";
import type {
    EliminationDetailTableCallbacks,
    EliminationDetailViewRow,
} from "./eliminationDetailViewConfig";
import styles from "./EliminationDetailTable.module.scss";

type EliminationCellContext = CellContext<
    ViewTableFeatures,
    EliminationDetailViewRow,
    unknown
>;

// Leading pinned column: account label on account rows, the balance-row labels
// on opening/closing rows, and the date on transaction rows (mirrors the GL
// "Account / Date" column).
export const EliminationNameCell = React.memo(function EliminationNameCell({
    row,
}: EliminationCellContext): React.ReactElement {
    const { rowKind, accountLabel, date } = row.original;

    if (rowKind === "account") {
        return (
            <ViewTableCell className={styles.accountCell}>
                {accountLabel}
            </ViewTableCell>
        );
    }
    if (rowKind === "openingBalance") {
        return (
            <ViewTableCell className={styles.balanceLabelCell}>
                Beginning Balance
            </ViewTableCell>
        );
    }
    if (rowKind === "closingBalance") {
        return (
            <ViewTableCell className={styles.endingBalanceLabelCell}>
                Ending Balance
            </ViewTableCell>
        );
    }
    if (date && date.length > 0) {
        return (
            <ViewTableCell>
                {format(parseISO(date), "MM/dd/yyyy")}
            </ViewTableCell>
        );
    }
    return <ViewTableCell />;
});

// The transaction counterparty (vendor/customer) on the underlying line, not
// the paired entity.
export const EliminationCounterpartyCell = React.memo(
    function EliminationCounterpartyCell({
        row,
    }: EliminationCellContext): React.ReactElement {
        if (row.original.rowKind !== "transaction") {
            return <ViewTableCell />;
        }
        return <ViewTableCell>{row.original.counterpartyName}</ViewTableCell>;
    },
);

export const EliminationEntityCell = React.memo(function EliminationEntityCell({
    row,
}: EliminationCellContext): React.ReactElement {
    const entitiesById = useEntitiesById();
    const entity =
        row.original.entityId === undefined
            ? undefined
            : entitiesById.get(row.original.entityId);
    if (row.original.rowKind !== "transaction") {
        return <ViewTableCell />;
    }
    if (!entity) {
        return <ViewTableCell>{row.original.entityName}</ViewTableCell>;
    }
    return (
        <ViewTableCell>
            <AvatarTooltip content={row.original.entityName ?? entity.name}>
                <EntityAvatar entity={entity} size="xs" copyId={entity.id} />
            </AvatarTooltip>
        </ViewTableCell>
    );
});

// Distinguishes engine-generated eliminations from manually authored ones,
// mirroring the GL Source column's role.
export const EliminationSourceCell = React.memo(function EliminationSourceCell({
    row,
}: EliminationCellContext): React.ReactElement {
    if (row.original.rowKind !== "transaction") {
        return <ViewTableCell />;
    }
    return (
        <ViewTableCell>
            {row.original.origin === "manual" ? "Manual" : "Auto"}
        </ViewTableCell>
    );
});

export const EliminationReferenceCell = React.memo(
    function EliminationReferenceCell({
        row,
        table,
    }: EliminationCellContext): React.ReactElement {
        const {
            rowKind,
            journalEntries,
            entityId,
            origin,
            manualJournalEntryId,
        } = row.original;
        const entitiesById = useEntitiesById();
        const { openOverlay } = useOverlays();
        const { callbacks } = useViewTableMeta<
            EliminationDetailTableCallbacks,
            never
        >(table);
        const entity =
            entityId !== undefined ? entitiesById.get(entityId) : undefined;
        const canOpenManualReference = Boolean(
            manualJournalEntryId &&
            callbacks?.onOpenEliminatingEntry &&
            callbacks.canOpenEliminatingEntry?.(manualJournalEntryId),
        );

        const openReference = useCallback(
            (referenceNumber: string, accountingLedgerId: string) => {
                if (origin === "manual") {
                    if (canOpenManualReference && manualJournalEntryId) {
                        callbacks?.onOpenEliminatingEntry?.(
                            manualJournalEntryId,
                        );
                    }
                    return;
                }
                if (!entity) {
                    return;
                }
                openOverlay("journalEntryDrawer", {
                    entity,
                    journalEntryId: referenceNumber,
                    ledgerId: accountingLedgerId,
                });
            },
            [
                callbacks,
                canOpenManualReference,
                entity,
                manualJournalEntryId,
                openOverlay,
                origin,
            ],
        );

        if (rowKind !== "transaction" || !journalEntries?.length) {
            return <ViewTableCell />;
        }

        return (
            <ViewTableCell captureClicks>
                {journalEntries.map((journalEntry, index) => (
                    <React.Fragment key={journalEntry.journalEntryId}>
                        {index > 0 && ", "}
                        <span
                            className={classNames({
                                [styles.clickableReference]:
                                    origin === "manual"
                                        ? canOpenManualReference
                                        : Boolean(entity),
                            })}
                            onClick={
                                origin !== "manual" || canOpenManualReference
                                    ? () =>
                                          openReference(
                                              journalEntry.referenceNumber,
                                              journalEntry.accountingLedgerId,
                                          )
                                    : undefined
                            }
                        >
                            {getShortJournalEntryReferenceNumber(
                                journalEntry.referenceNumber,
                            )}
                        </span>
                    </React.Fragment>
                ))}
            </ViewTableCell>
        );
    },
);

export const EliminationDescriptionCell = React.memo(
    function EliminationDescriptionCell({
        row,
    }: EliminationCellContext): React.ReactElement {
        const { rowKind, description } = row.original;

        if (rowKind !== "transaction" || !description) {
            return <ViewTableCell />;
        }

        return <ViewTableCell>{description}</ViewTableCell>;
    },
);

// Elimination rows carry no class dimension, so this renders empty on every
// row kind (the GL renders the same way for lines without a class).
export const EliminationClassCell = React.memo(function EliminationClassCell(
    _props: EliminationCellContext,
): React.ReactElement {
    return <ViewTableCell />;
});

export const EliminationSplitCell = React.memo(function EliminationSplitCell({
    row,
}: EliminationCellContext): React.ReactElement {
    const { rowKind, split } = row.original;

    if (rowKind !== "transaction" || !split) {
        return <ViewTableCell />;
    }

    return <ViewTableCell>{split}</ViewTableCell>;
});

export const EliminationAmountCell = React.memo(function EliminationAmountCell({
    row,
}: EliminationCellContext): React.ReactElement {
    if (row.original.rowKind !== "transaction") {
        return <ViewTableCell />;
    }
    return (
        <ViewTableCell className={styles.numericCell}>
            {accountingNumberFormatter(row.original.amount ?? 0)}
        </ViewTableCell>
    );
});

// Account rows show the account's closing total when collapsed (hidden when
// expanded, since the Ending balance child row then carries it); opening/closing
// rows show their balance value; transaction rows show the running balance.
export const EliminationBalanceCell = React.memo(
    function EliminationBalanceCell({
        row,
    }: EliminationCellContext): React.ReactElement {
        const { rowKind, closingBalance, balanceValue, balance } = row.original;

        if (rowKind === "account") {
            return (
                <RowExpansion row={row}>
                    {(isExpanded) =>
                        isExpanded ? (
                            <ViewTableCell />
                        ) : (
                            <ViewTableCell
                                className={classNames(
                                    styles.numericCell,
                                    styles.accountCell,
                                )}
                            >
                                {accountingNumberFormatter(closingBalance ?? 0)}
                            </ViewTableCell>
                        )
                    }
                </RowExpansion>
            );
        }

        if (rowKind === "openingBalance" || rowKind === "closingBalance") {
            return (
                <ViewTableCell
                    className={classNames(styles.numericCell, {
                        [styles.endingBalanceLabelCell]:
                            rowKind === "closingBalance",
                    })}
                >
                    {accountingNumberFormatter(balanceValue ?? 0)}
                </ViewTableCell>
            );
        }

        if (rowKind === "transaction") {
            return (
                <ViewTableCell className={styles.numericCell}>
                    {accountingNumberFormatter(balance ?? 0)}
                </ViewTableCell>
            );
        }

        return <ViewTableCell />;
    },
);
